import { Link } from "react-router-dom";
import Select from "react-select";
import { cn } from "../../../utils/classnames";
import { BackIcon } from "../../icons";
import { adminSelectStyles } from "./AdminFilterBar";
import { SelectDropdownIndicator } from "./Icons";

/**
 * The create/edit counterpart to AdminPage + AdminTable.
 *
 * The list screens already settled the admin dashboard's design language — Hanken
 * Grotesk on #212529, 45px controls with a #D9D9D9 hairline and a 5px radius, muted
 * #7C7B7B labels, a solid #282828 action button. The create/edit forms behind them
 * were still on raw Bootstrap (`card`, `form-control-admin`, `btn btn-primary`), so
 * every one of them looked like a different product from the list it was reached from.
 *
 * These primitives are that language applied to form controls. A screen composes
 * AdminFormCard sections out of the *Field components and closes with AdminFormActions;
 * nothing below needs a Bootstrap class.
 */

// --- shared control chrome -------------------------------------------------

const CONTROL_BASE =
    "tw:h-[45px] tw:w-full tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:px-[15px] " +
    "tw:text-[15px] tw:leading-[22px] tw:!text-[#282828] tw:font-hanken-grotesk " +
    "tw:placeholder:!text-[#7C7B7B] tw:focus:!outline-none tw:focus:!border-[#282828]";

const DISABLED = "tw:!bg-[#F7F7F7] tw:!text-[#7C7B7B] tw:cursor-not-allowed";

// --- layout ----------------------------------------------------------------

/** A titled block of fields. `columns` lays the fields out on wider screens. */
export const AdminFormCard = ({ title, description, columns = 1, className, children }) => (
    <section
        className={cn(
            "tw:mt-[24px] tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:bg-white",
            className
        )}
    >
        {title ? (
            <div className="tw:border-0 tw:border-b tw:border-solid tw:border-[#E6E6E6] tw:px-[20px] tw:py-[16px]">
                <div className="tw:text-[16px] tw:font-semibold tw:leading-[normal] tw:text-[#212529]">
                    {title}
                </div>
                {description ? (
                    <div className="tw:mt-1 tw:text-[14px] tw:leading-5 tw:text-[#7C7B7B]">
                        {description}
                    </div>
                ) : null}
            </div>
        ) : null}

        <div
            className={cn(
                "tw:grid tw:gap-x-[20px] tw:gap-y-[18px] tw:p-[20px]",
                columns === 2 ? "tw:md:grid-cols-2" : "tw:grid-cols-1"
            )}
        >
            {children}
        </div>
    </section>
);

/** Makes one field span the full width of a two-column AdminFormCard. */
export const AdminFormFullRow = ({ className, children }) => (
    <div className={cn("tw:md:col-span-2", className)}>{children}</div>
);

/** Label + control + hint/error. Every field below is built on this. */
export const AdminField = ({ label, hint, error, htmlFor, required, children }) => (
    <div>
        {label ? (
            <label
                htmlFor={htmlFor}
                className="tw:mb-2 tw:!block tw:text-[14px] tw:leading-5 tw:!text-[#7C7B7B]"
            >
                {label}
                {required ? <span className="tw:ml-1 tw:text-[#EA4335]">*</span> : null}
            </label>
        ) : null}

        {children}

        {error ? (
            <div className="tw:mt-1 tw:text-[13px] tw:leading-5 tw:text-[#EA4335]">{error}</div>
        ) : hint ? (
            <div className="tw:mt-1 tw:text-[13px] tw:leading-5 tw:text-[#7C7B7B]">{hint}</div>
        ) : null}
    </div>
);

// --- controls --------------------------------------------------------------

export const AdminTextField = ({
    label,
    hint,
    error,
    required,
    name,
    type = "text",
    className,
    ...inputProps
}) => (
    <AdminField label={label} hint={hint} error={error} htmlFor={name} required={required}>
        <input
            id={name}
            name={name}
            type={type}
            required={required}
            // A focused number input treats the wheel as a value change, so scrolling
            // the page over one silently edits it. Dropping focus hands the gesture
            // back to the page, which is what the user meant by it.
            onWheel={type === "number" ? (e) => e.target.blur() : undefined}
            className={cn(
                CONTROL_BASE,
                error && "tw:!border-[#EA4335]",
                inputProps.disabled && DISABLED,
                className
            )}
            {...inputProps}
        />
    </AdminField>
);

export const AdminTextAreaField = ({
    label,
    hint,
    error,
    required,
    name,
    rows = 4,
    className,
    ...textAreaProps
}) => (
    <AdminField label={label} hint={hint} error={error} htmlFor={name} required={required}>
        <textarea
            id={name}
            name={name}
            rows={rows}
            required={required}
            className={cn(
                CONTROL_BASE,
                "tw:!h-auto tw:py-[11px] tw:leading-[22px]",
                error && "tw:!border-[#EA4335]",
                className
            )}
            {...textAreaProps}
        />
    </AdminField>
);

/**
 * react-select wearing the list pages' control styling.
 *
 * `isSearchable` defaults to true here (unlike the filter bar's): these are pickers
 * over vehicle types and icon sets, which are long enough to want typing.
 */
export const AdminSelectField = ({
    label,
    hint,
    error,
    required,
    name,
    placeholder = "Select...",
    component: SelectComponent = Select,
    ...selectProps
}) => (
    <AdminField label={label} hint={hint} error={error} htmlFor={name} required={required}>
        <SelectComponent
            inputId={name}
            name={name}
            placeholder={placeholder}
            styles={adminSelectStyles}
            components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
            {...selectProps}
        />
    </AdminField>
);

/** An on/off switch with its own label and explanatory line. */
export const AdminToggleField = ({ label, description, checked, onChange, name, disabled }) => (
    <label
        htmlFor={name}
        className={cn(
            // Bootstrap's un-layered `label { display: inline-block }` outranks a
            // layered tw: utility, so the display has to be forced here.
            "tw:!flex tw:cursor-pointer tw:items-start tw:gap-3",
            disabled && "tw:cursor-not-allowed tw:opacity-60"
        )}
    >
        <span className="tw:relative tw:mt-[2px] tw:!inline-flex tw:h-[22px] tw:w-[40px] tw:shrink-0">
            <input
                id={name}
                name={name}
                type="checkbox"
                checked={!!checked}
                disabled={disabled}
                onChange={onChange}
                className="tw:peer tw:h-0 tw:w-0 tw:opacity-0"
            />
            <span className="tw:absolute tw:inset-0 tw:rounded-full tw:bg-[#D9D9D9] tw:transition-colors tw:peer-checked:bg-[#282828]" />
            <span className="tw:absolute tw:top-[3px] tw:left-[3px] tw:h-[16px] tw:w-[16px] tw:rounded-full tw:bg-white tw:transition-transform tw:peer-checked:translate-x-[18px]" />
        </span>

        <span className="tw:min-w-0">
            <span className="tw:block tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">
                {label}
            </span>
            {description ? (
                <span className="tw:mt-0.5 tw:block tw:text-[13px] tw:leading-5 tw:text-[#7C7B7B]">
                    {description}
                </span>
            ) : null}
        </span>
    </label>
);

/** A row of radio options — `options` is `[{ value, label }]`. */
export const AdminRadioField = ({ label, hint, name, value, options, onChange }) => (
    <AdminField label={label} hint={hint}>
        <div className="tw:flex tw:flex-wrap tw:gap-x-[24px] tw:gap-y-[10px] tw:pt-[6px]">
            {options.map((option) => (
                <label
                    key={String(option.value)}
                    className="tw:!flex tw:cursor-pointer tw:items-center tw:gap-2 tw:text-[15px] tw:leading-[22px] tw:text-[#282828]"
                >
                    <input
                        type="radio"
                        name={name}
                        checked={value === option.value}
                        onChange={() => onChange(option.value)}
                        className="tw:h-[16px] tw:w-[16px] tw:cursor-pointer tw:accent-[#282828]"
                    />
                    {option.label}
                </label>
            ))}
        </div>
    </AdminField>
);

// --- actions ---------------------------------------------------------------

/** "Back to list", rendered in the page header via <AdminPage actions={...} />. */
export const AdminBackLink = ({ to, label = "Back to list" }) => (
    <Link
        to={to}
        className="tw:!flex tw:h-[45px] tw:items-center tw:gap-2 tw:rounded-[5px] tw:border tw:border-solid tw:border-[#282828] tw:bg-white tw:px-[17px] tw:text-[15px] tw:leading-[22px] tw:!text-[#282828] tw:font-hanken-grotesk tw:!no-underline"
    >
        <BackIcon color="#282828" />
        {label}
    </Link>
);

export const AdminFormButton = ({ variant = "solid", disabled, className, children, ...props }) => (
    <button
        disabled={disabled}
        className={cn(
            "tw:!flex tw:h-[45px] tw:min-w-[140px] tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:px-[20px] tw:text-[15px] tw:leading-[22px] tw:font-hanken-grotesk tw:cursor-pointer tw:disabled:cursor-not-allowed tw:disabled:opacity-60",
            variant === "solid"
                ? "tw:border-0 tw:bg-[#282828] tw:font-bold tw:!text-white"
                : "tw:border tw:border-solid tw:border-[#282828] tw:bg-white tw:!text-[#282828]",
            className
        )}
        {...props}
    >
        {children}
    </button>
);

/** The action row that closes a form. */
export const AdminFormActions = ({ children }) => (
    <div className="tw:mt-[24px] tw:flex tw:flex-wrap tw:items-center tw:gap-3">{children}</div>
);
